import React from 'react';

/**
 * Declarative definition of everything a candidate fills in about themselves.
 * Defined at module scope (and rendered by a module-scope Field component) so inputs are
 * not remounted on every keystroke, which would drop focus.
 */
export const CANDIDATE_SECTIONS = [
  {
    title: 'Personal Details',
    fields: [
      { name: 'name', label: 'Full Name', type: 'text', required: true },
      { name: 'dateOfBirth', label: 'Date of Birth', type: 'date', required: true },
      { name: 'gender', label: 'Gender', type: 'select', required: true, options: [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other', label: 'Other' },
      ] },
      { name: 'maritalStatus', label: 'Marital Status', type: 'select', required: true, options: [
        { value: 'single', label: 'Single' },
        { value: 'married', label: 'Married' },
      ] },
      { name: 'bloodGroup', label: 'Blood Group', type: 'text' },
      { name: 'physicalChallenged', label: 'Physically Challenged', type: 'checkbox' },
    ],
  },
  {
    title: 'Contact Details',
    fields: [
      { name: 'mobileNumber', label: 'Mobile Number', type: 'text', required: true },
      { name: 'alternateNumber', label: 'Alternate Number', type: 'text', required: true },
      { name: 'emergencyName', label: 'Emergency Contact Name', type: 'text' },
      { name: 'emergencyRelation', label: 'Emergency Contact Relation', type: 'text' },
      { name: 'emergencyContactNumber', label: 'Emergency Contact Number', type: 'text', required: true },
    ],
  },
  {
    title: 'Address',
    fields: [
      { name: 'presentAddress', label: 'Current Address', type: 'textarea', required: true, full: true },
      { name: 'permanentAddress', label: 'Permanent Address', type: 'textarea', required: true, full: true },
      { name: 'city', label: 'City', type: 'text', required: true },
      { name: 'state', label: 'State', type: 'text', required: true },
      { name: 'pinCode', label: 'Pin Code', type: 'text', required: true },
    ],
  },
  {
    title: 'KYC',
    fields: [
      { name: 'panNumber', label: 'PAN Number', type: 'text', required: true },
      { name: 'aadhaarNumber', label: 'Aadhaar Number', type: 'text', required: true },
      { name: 'passportNumber', label: 'Passport Number', type: 'text' },
      { name: 'pfDetails', label: 'PF Details', type: 'text' },
      { name: 'medicalInsurance', label: 'Medical Insurance', type: 'text' },
    ],
  },
  {
    title: 'Education',
    fields: [
      { name: 'schoolName', label: 'School Name', type: 'text' },
      { name: 'schoolBoard', label: 'School Board', type: 'text' },
      { name: 'schoolYearOfGraduation', label: 'School Passing Year', type: 'number' },
      { name: 'schoolPercentageOrCGPA', label: 'School Percentage / CGPA', type: 'number' },
      { name: 'universityName', label: 'University / College Name', type: 'text' },
      { name: 'universityBoard', label: 'University Board', type: 'text' },
      { name: 'universityYearOfGraduation', label: 'University Passing Year', type: 'number' },
      { name: 'universityPercentageOrCGPA', label: 'University Percentage / CGPA', type: 'number' },
      { name: 'postGraduationName', label: 'Post Graduation Name', type: 'text' },
      { name: 'postGraduationBoard', label: 'Post Graduation Board', type: 'text' },
      { name: 'postGraduationYearOfGraduation', label: 'Post Graduation Passing Year', type: 'number' },
      { name: 'postGraduationPercentageOrCGPA', label: 'Post Graduation Percentage / CGPA', type: 'number' },
      { name: 'highestEducationCategory', label: 'Highest Qualification', type: 'text' },
      { name: 'highestEducationInstitutionName', label: 'Highest Qualification Institution', type: 'text' },
      { name: 'highestEducationBoard', label: 'Highest Qualification Board', type: 'text' },
      { name: 'highestEducationYearOfPassing', label: 'Highest Qualification Year', type: 'number' },
      { name: 'highestEducationPercentageOrCGPA', label: 'Highest Qualification Percentage / CGPA', type: 'number' },
    ],
  },
  {
    title: 'References',
    fields: [
      { name: 'reference1Name', label: 'Reference 1 Name', type: 'text' },
      { name: 'reference1Relationship', label: 'Reference 1 Relationship', type: 'text' },
      { name: 'reference1Mobile', label: 'Reference 1 Mobile', type: 'text' },
      { name: 'reference1EmergencyContactNumber', label: 'Reference 1 Emergency Contact Number', type: 'text' },
      { name: 'reference1Address', label: 'Reference 1 Address', type: 'textarea', full: true },
      { name: 'reference2Name', label: 'Reference 2 Name', type: 'text' },
      { name: 'reference2Relationship', label: 'Reference 2 Relationship', type: 'text' },
      { name: 'reference2Mobile', label: 'Reference 2 Mobile', type: 'text' },
      { name: 'reference2EmergencyContactNumber', label: 'Reference 2 Emergency Contact Number', type: 'text' },
      { name: 'reference2Address', label: 'Reference 2 Address', type: 'textarea', full: true },
    ],
  },
  {
    title: 'Previous Employment',
    experiencedOnly: true,
    fields: [
      { name: 'totalExperience', label: 'Total Experience (years)', type: 'number' },
      { name: 'previousCompanyName', label: 'Previous Company', type: 'text' },
      { name: 'previousCompanyLocation', label: 'Previous Company Location', type: 'text' },
      { name: 'previousJobTitle', label: 'Previous Job Title', type: 'text' },
      { name: 'previousSupervisorName', label: 'Previous Supervisor Name', type: 'text' },
      { name: 'previousSupervisorContact', label: 'Previous Supervisor Contact', type: 'text' },
      { name: 'previousSupervisorEmail', label: 'Previous Supervisor Email', type: 'email' },
      { name: 'previousStartDate', label: 'Previous Start Date', type: 'date' },
      { name: 'previousEndDate', label: 'Previous End Date', type: 'date' },
      { name: 'reasonForLeaving', label: 'Reason For Leaving', type: 'textarea', full: true },
      { name: 'relievingLetterSubmitted', label: 'Relieving Letter Submitted', type: 'checkbox' },
    ],
  },
  {
    title: 'Acknowledgement',
    fields: [
      { name: 'acknowledgementAccepted', label: 'I confirm that all information provided is correct and true to the best of my knowledge.', type: 'checkbox', required: true, full: true },
      { name: 'candidateSignature', label: 'Applicant Signature (type your full name)', type: 'text', required: true },
      { name: 'candidateDeclarationDate', label: 'Declaration Date', type: 'date', required: true },
    ],
  },
];

export function sectionsForEmploymentType(employmentType) {
  return CANDIDATE_SECTIONS.filter((s) => !s.experiencedOnly || employmentType === 'experienced');
}

export function allCandidateFields(employmentType) {
  return sectionsForEmploymentType(employmentType).flatMap((s) => s.fields);
}

const inputCls =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors disabled:bg-gray-50 disabled:text-gray-500';

/** Module-scope so React keeps the same element identity between renders. */
export const Field = ({ field, value, onChange, disabled }) => {
  const { name, label, type, required, options, full } = field;

  if (type === 'checkbox') {
    return (
      <div className={full ? 'md:col-span-2' : ''}>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={!!value}
            disabled={disabled}
            onChange={(e) => onChange(name, e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          {label}
        </label>
      </div>
    );
  }

  return (
    <div className={full ? 'md:col-span-2' : ''}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {type === 'select' ? (
        <select
          value={value ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(name, e.target.value)}
          className={inputCls}
        >
          <option value="">Select {label}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea
          rows={3}
          value={value ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(name, e.target.value)}
          className={inputCls}
        />
      ) : (
        <input
          type={type}
          value={value ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(name, e.target.value)}
          className={inputCls}
        />
      )}
    </div>
  );
};
